import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { DashboardService } from './dashboard.service'
import { AuthenticationGuard } from '../services/authentication-guard.service';
import { formatDisplayDate } from 'app/shared/utils/date-format.util';

@Component({
  selector: 'app-dashboard',
  providers: [DashboardService],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit {
  maxResults = 10;
  myClients = [];
  myTasks = [];
  myTasksOffset = 0;
  myReviews = [];
  myOnboardings = [];
  newsItems = [];
  showAndHide: boolean[];

  // RS: current loading implementation can't handle empty results.
  // hence, we keep the flags that initiate them on false for now.
  loadingNews = false;
  loadingMyOnboardings = false;
  loadingMyReviews = false;
  loadingMyTasks = false;
  loadingMyClients = false;
  loadingMonitoringHitsForClients = false;
  openSanctionMatchesForClients: any[] = [];
  openPepMatchesForClients: any[] = [];
  openNegativeNewsForClients: any[] = [];
  openLeakPapersForClients: any[] = [];
  monitoringHitsForClientsTotal = 0;

  groupedOpenSanctionMatches: any[] = [];
  groupedOpenPepMatches: any[] = [];
  groupedOpenNegativeNews: any[] = [];
  groupedOpenLeakPapers: any[] = [];

  /** Which combined card tab is visible: tasks vs screening hits. */
  activeTasksOrScreeningTab: 'tasks' | 'screening' = 'tasks';

  constructor(
    private dashboardService: DashboardService,
    private router: Router,
    private authenticationGuard: AuthenticationGuard,
  ) { }

  /** Tab labels include counts once each section has finished loading. */
  get tasksTabHeader(): string {
    if (!this.loadingMyTasks) {
      return 'My Tasks';
    }
    return `My Tasks (${this.myTasks.length})`;
  }

  get screeningTabHeader(): string {
    if (!this.loadingMonitoringHitsForClients) {
      return 'Screening hits';
    }
    return `Screening hits (${this.monitoringHitsForClientsTotal})`;
  }

  ngOnInit() {
    // redirect to dnbdashboard if dnb user
    if (this.authenticationGuard.isDnbUser())
      this.router.navigate(['/dnbdashboard']);

    this.getMyClients();
    this.getMyTasks();
    this.getMyReviews();
    this.getMyOnboardings();
    this.getNews();
    this.getScreeningHitsForMyClients();
  }

  getFormattedDate(dateMs: string) {
    return formatDisplayDate(dateMs);
  }

  getMyClients() {
    this.dashboardService.fetchMyClients()
      .subscribe(
      response => {
        this.loadingMyClients = true;
        if (response.arrResult.length == 0) {
            return;
          }
          this.myClients = this.myClients.concat(response.arrResult);
      }
    );
  }

  getMyTasks() {
    this.dashboardService.fetchMyTasks(this.myTasksOffset, this.maxResults)
      .subscribe(
      response => {
        this.loadingMyTasks = true;
        if (response.arrResult.length == 0) {
          return;
        }
        this.myTasks = this.myTasks.concat(response.arrResult);
        this.myTasksOffset += 1;
      });
  }

  getMyReviews() {
    this.dashboardService.fetchMyReviews()
      .subscribe(
      response => {
        this.loadingMyReviews = true;
        if (response.arrResult.length == 0) {
          return;
        }
        this.myReviews = this.myReviews.concat(response.arrResult);
      });
  }

  getMyOnboardings() {
    this.dashboardService.fetchMyOnboardings()
      .subscribe(
      response => {
        this.loadingMyOnboardings = true;
        if (response.arrResult.length == 0) {
          return;
        }
        this.myOnboardings = this.myOnboardings.concat(response.arrResult);
      });
  }

  getNews() {
    this.dashboardService.fetchNews()
      .subscribe(
      response => {
        this.loadingNews = true;
        if (response.arrResult.length == 0) {
          return;
        }
        this.newsItems = this.newsItems.concat(response.arrResult);
      });
  }

  getScreeningHitsForMyClients() {
    this.dashboardService.fetchScreeningHitsForMyClients()
      .subscribe(
        (response: any) => {
          this.loadingMonitoringHitsForClients = true;
          const r = response && response.arrResult != null ? response.arrResult : response;
          if (!r || typeof r !== 'object') {
            return;
          }
          this.openSanctionMatchesForClients = r.openSanctionMatches || [];
          this.openPepMatchesForClients = r.openPepMatches || [];
          this.openNegativeNewsForClients = r.openNegativeNews || [];
          this.openLeakPapersForClients = r.openLeakPapers || [];
          this.monitoringHitsForClientsTotal = r.totalCount != null
            ? r.totalCount
            : this.openSanctionMatchesForClients.length
                + this.openPepMatchesForClients.length
                + this.openNegativeNewsForClients.length
                + this.openLeakPapersForClients.length;
          this.rebuildGroupedScreeningHits();
        },
        () => {
          this.loadingMonitoringHitsForClients = true;
          this.openSanctionMatchesForClients = [];
          this.openPepMatchesForClients = [];
          this.openNegativeNewsForClients = [];
          this.openLeakPapersForClients = [];
          this.monitoringHitsForClientsTotal = 0;
          this.rebuildGroupedScreeningHits();
        });
  }

  private rebuildGroupedScreeningHits() {
    this.groupedOpenSanctionMatches =
      this.groupByEntity(this.openSanctionMatchesForClients, 'sanctionGlobalName');
    this.groupedOpenPepMatches =
      this.groupByEntity(this.openPepMatchesForClients, 'pepGlobalName');
    this.groupedOpenNegativeNews =
      this.groupByEntity(this.openNegativeNewsForClients, 'badPressName');
    this.groupedOpenLeakPapers =
      this.groupByEntity(this.openLeakPapersForClients, 'badPressName');
  }

  /**
   * Collapse per-hit rows into one row per entity. Preserves first-seen order and
   * collects the distinct matched names (aliases / list variants) so the UI can show
   * them as a subtitle.
   */
  private groupByEntity(rows: any[], nameField: string): any[] {
    const map: { [id: number]: any } = {};
    const order: number[] = [];
    for (const r of rows) {
      let g = map[r.entityId];
      if (!g) {
        g = { entityId: r.entityId, entityName: r.entityName, count: 0, names: [] };
        map[r.entityId] = g;
        order.push(r.entityId);
      }
      g.count++;
      const nm = r[nameField];
      if (nm && g.names.indexOf(nm) === -1) { g.names.push(nm); }
    }
    return order.map(id => map[id]);
  }
}



// WEBPACK FOOTER //
// ./src/app/dashboard/dashboard.component.ts